<template>
  <div id="app">
    <!-- 1. ADD THIS POPUP TO YOUR TEMPLATE (after the volume breakdown popup) -->
<div v-if="showEventsBreakdown" class="simple-popup" @click.self="showEventsBreakdown = false">
  <div class="popup-content">
    <div class="popup-header">
      <h3>Events Breakdown - {{ eventsBreakdownTitle }}</h3>
      <button @click="showEventsBreakdown = false" class="close-btn">×</button>
    </div>
    <div class="popup-body">
      <div class="events-summary">
        <div class="summary-item">
          <span class="summary-label">Total Events:</span>
          <span class="summary-value">{{ totalEventsInRange }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Time Period:</span>
          <span class="summary-value">{{ currentPeriodLabel }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Active Devices:</span>
          <span class="summary-value">{{ eventsByDevice.length }}</span>
        </div>
      </div>

      <div class="events-list-container">
        <h4>Events by FAMS Device</h4>
        <div v-for="device in eventsByDevice" :key="device.deviceId" class="events-device-item">
          <div class="device-header" @click="device.expanded = !device.expanded">
            <div class="device-info-row">
              <div class="device-name-section">
                <strong>{{ device.deviceId }}</strong>
                <span class="device-alias">{{ device.alias || 'No alias' }}</span>
              </div>
              <div class="device-count">
                <span class="event-count-badge">{{ device.totalEvents }} events</span>
                <span class="expand-icon">{{ device.expanded ? '▼' : '▶' }}</span>
              </div>
            </div>
            <div class="device-progress-bar">
              <div class="progress-fill" :style="{ width: device.percentage + '%' }"></div>
            </div>
          </div>

          <div v-if="device.expanded" class="device-events-detail">
            <div class="event-type-grid">
              <div v-for="eventType in device.eventTypes" :key="eventType.type" class="event-type-item">
                <span :class="['activity-badge', 'badge-' + eventType.type]">
                  {{ eventType.type }}
                </span>
                <span class="event-count">{{ eventType.count }}</span>
              </div>
            </div>
            <div class="device-timeline">
              <div class="timeline-item">
                <span class="timeline-label">First Event:</span>
                <span class="timeline-value">{{ formatDateTime(device.firstEvent) }}</span>
              </div>
              <div class="timeline-item">
                <span class="timeline-label">Last Event:</span>
                <span class="timeline-value">{{ formatDateTime(device.lastEvent) }}</span>
              </div>
              <div class="timeline-item">
                <span class="timeline-label">Peak Hour:</span>
                <span class="timeline-value">{{ device.peakHour }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="eventsByDevice.length === 0" class="no-events">
          No events found in the selected time range.
        </div>
      </div>
    </div>
  </div>
</div>

    <!-- 1. ADD THIS POPUP TO YOUR TEMPLATE (after the device list popup) -->
  <div v-if="showVolumeBreakdown" class="simple-popup" @click.self="showVolumeBreakdown = false">
    <div class="popup-content">
      <div class="popup-header">
        <h3>Volume Breakdown - {{ (stats.totalVolume || 0).toFixed(2) }}L Total</h3>
        <button @click="showVolumeBreakdown = false" class="close-btn">×</button>
      </div>
      <div class="popup-body">
        <div class="volume-summary">
          <div class="summary-item">
            <span class="summary-label">Total Dispensed:</span>
            <span class="summary-value">{{ (stats.totalVolume || 0).toFixed(2) }}L</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Average per Event:</span>
            <span class="summary-value">{{ (stats.avgVolume || 0).toFixed(2) }}L</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Events with Volume:</span>
            <span class="summary-value">{{ volumeEventCount }}</span>
          </div>
        </div>

        <div class="volume-list-container">
          <h4>Volume by Device</h4>
          <div v-for="device in volumeByDevice" :key="device.deviceId" class="volume-item">
            <div class="volume-device-info">
              <div class="device-name">
                <strong>{{ device.deviceId }}</strong>
                <span class="device-alias">{{ device.alias || '' }}</span>
              </div>
              <div class="volume-amount">
                <span class="volume-value">{{ device.totalVolume.toFixed(2) }}L</span>
                <span class="volume-percentage">({{ device.percentage.toFixed(1) }}%)</span>
              </div>
            </div>
            <div class="volume-bar-container">
              <div class="volume-bar" :style="{ width: device.percentage + '%' }"></div>
            </div>
            <div class="volume-details">
              <span class="detail-badge">{{ device.eventCount }} events</span>
              <span class="detail-badge">Avg: {{ device.avgVolume.toFixed(2) }}L</span>
              <span class="detail-badge">Max: {{ device.maxVolume.toFixed(2) }}L</span>
            </div>
          </div>

          <div v-if="volumeByDevice.length === 0" class="no-volume">
            No volume data found in the selected time range.
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- 1. ADD THIS POPUP TO YOUR TEMPLATE (after the activity details popup) -->
  <div v-if="showDeviceList" class="simple-popup" @click.self="showDeviceList = false">
    <div class="popup-content">
      <div class="popup-header">
        <h3>Active Devices ({{ uniqueDevices.length }})</h3>
        <button @click="showDeviceList = false" class="close-btn">×</button>
      </div>
      <div class="popup-body">
        <div class="device-list-container">
          <div v-for="device in deviceListWithStats" :key="device.id" class="device-list-item">
            <div class="device-main-info">
              <strong>{{ device.id }}</strong>
              <span class="device-alias">{{ device.alias || 'No alias' }}</span>
            </div>
            <div class="device-stats">
              <span class="device-stat-badge">{{ device.eventCount }} events</span>
              <span class="device-stat-badge volume">{{ device.totalVolume.toFixed(2) }}L</span>
              <span class="device-stat-badge" :class="'status-' + device.lastStatus">{{ device.lastStatus }}</span>
            </div>
          </div>
          <div v-if="uniqueDevices.length === 0" class="no-devices">
            No active devices found in the selected time range.
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- 1. ADD THIS TO YOUR TEMPLATE (right after opening <div id="app">) -->
    <!-- Simple Popup Modal -->
    <div v-if="selectedActivity" class="simple-popup" @click.self="selectedActivity = null">
      <div class="popup-content">
        <div class="popup-header">
          <h3>Activity Details</h3>
          <button @click="selectedActivity = null" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="detail-row">
            <strong>Date:</strong> {{ formatDateTime(selectedActivity.timestamp || selectedActivity.createDate) }}
          </div>
          <div class="detail-row">
            <strong>Device ID:</strong> {{ selectedActivity.deviceId }}
          </div>
          <div class="detail-row">
            <strong>Device Alias:</strong> {{ selectedActivity.deviceAlias || '-' }}
          </div>
          <div class="detail-row">
            <strong>Store:</strong> {{ selectedActivity.storeName || '-' }}
          </div>
          <div class="detail-row">
            <strong>Activity Type:</strong> <span :class="['activity-badge', 'badge-' + selectedActivity.type]">{{ selectedActivity.type }}</span>
          </div>
          <div class="detail-row">
            <strong>Transaction ID:</strong> {{ selectedActivity.transactionId || selectedActivity.transactionID }}
          </div>
          <div class="detail-row">
            <strong>Volume:</strong> {{ selectedActivity.volume ? selectedActivity.volume.toFixed(2) + ' L' : '-' }}
          </div>
          <div class="detail-row">
            <strong>Status:</strong> <span :class="'status-' + selectedActivity.status">{{ selectedActivity.status }}</span>
          </div>
          <div class="detail-row">
            <strong>Message:</strong><br>
            <div class="message-text">{{ selectedActivity.message }}</div>
          </div>
        </div>
      </div>
    </div>
    <!-- Header -->
    <div class="header-toolbar">
      <div class="header-content">
        <div class="logo-section">
          <div class="logo-icon">
            <span class="icon-pulse">⚡</span>
          </div>
          <h1 class="dashboard-title">FAMS Device Activity Dashboard</h1>
        </div>
        <div class="header-controls">
          <div class="loader-controls">
            <div class="loader-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="showLoaderOption" class="toggle-checkbox">
                <span class="toggle-text">Show Loader</span>
              </label>
            </div>
            <div v-if="showLoaderOption" class="loader-speed">
              <label class="speed-label">
                <span class="speed-text">Speed:</span>
                <select v-model="minLoaderTime" class="speed-select">
                  <option :value="0">Instant</option>
                  <option :value="500">Fast (0.5s)</option>
                  <option :value="1000">Normal (1s)</option>
                  <option :value="1500">Smooth (1.5s)</option>
                  <option :value="2000">Slow (2s)</option>
                  <option :value="3000">Very Slow (3s)</option>
                </select>
              </label>
            </div>
            <div class="auto-refresh-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle" class="toggle-checkbox">
                <span class="toggle-text">Auto Refresh</span>
              </label>
            </div>
            <div v-if="autoRefreshEnabled" class="refresh-interval">
              <label class="interval-label">
                <span class="interval-text">Every:</span>
                <select v-model="refreshInterval" @change="handleRefreshIntervalChange" class="interval-select">
                  <option :value="15000">15 sec</option>
                  <option :value="30000">30 sec</option>
                  <option :value="60000">1 min</option>
                  <option :value="300000">5 min</option>
                  <option :value="600000">10 min</option>
                  <option :value="1800000">30 min</option>
                </select>
              </label>
            </div>
          </div>
          <div class="live-indicator">
            <div :class="['live-dot', { 'error': errorCount >= 3, 'auto-refresh': autoRefreshEnabled }]"></div>
            <span style="color: white; font-size: 0.875rem;">
              {{ autoRefreshEnabled ? `Live (${formatRefreshInterval()})` : connectionStatus }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Loader Overlay -->
    <div v-if="showLoaderOption && isLoading" class="global-loader-overlay">
      <div class="global-loader-content">
        <div class="global-loader"></div>
        <div class="loader-text">{{ loaderMessage }}</div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Statistics Section -->
      <div class="stats-section">
        <div class="stats-header">
          <h3 class="stats-title">Statistics Overview</h3>
          <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
            <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
            {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
          </button>
        </div>

        <transition name="slide-fade">
          <div v-if="statsVisible" class="stats-wrapper">
            <!-- Statistics Cards -->
            <div class="stats-container">
             <div class="stat-card clickable-stat" @click="openEventsBreakdown" title="Click to see events breakdown">
                <!-- <div class="stat-label">Total Events Today</div> -->
                 <div class="stat-label">
                  <!-- {{ (filters.startDateTime || filters.endDateTime) ? 'Total Events in Ranges' : 'Total Events Today' }} -->
                     {{ getStatsDateRangeLabel() }}
                </div>
                <div class="stat-value">{{ stats.totalActivities || 0 }}</div>
                <div class="stat-change" :class="(stats.eventsTrend || 0) > 0 ? 'positive' : 'negative'">
                  <span class="stat-icon">{{ (stats.eventsTrend || 0) > 0 ? '📈' : '📉' }}</span>
                  <!-- <span>{{ Math.abs(stats.eventsTrend || 0).toFixed(1) }}% from yesterday</span> -->
                   <span>
                      {{ (filters.startDateTime || filters.endDateTime)
                        ? Math.abs(stats.eventsTrend || 0).toFixed(1) + '% change in period'
                        : Math.abs(stats.eventsTrend || 0).toFixed(1) + '% from yesterday'
                      }}
                    </span>
                </div>
              </div>

              <div class="stat-card clickable-stat" @click="openDeviceList" title="Click to see device list">
                <div class="stat-label">Active Devices</div>
                <div class="stat-value">{{ stats.totalDevices || 0 }}</div>
                <div class="stat-change positive">
                  <span class="stat-icon">✓</span>
                  <span>All systems operational</span>
                </div>
              </div>

              <div class="stat-card clickable-stat" @click="openVolumeBreakdown" title="Click to see volume breakdown">
                <div class="stat-label">Total Volume (L)</div>
                <div class="stat-value">{{ (stats.totalVolume || 0).toFixed(2) }}</div>
                <div class="stat-change positive">
                  <span class="stat-icon">💧</span>
                  <span>{{ (stats.avgVolume || 0).toFixed(1) }}L average</span>
                </div>
              </div>

              <div class="stat-card">
                <div class="stat-label">Processing Rate</div>
                <div class="stat-value">{{ (stats.processingRate || 0).toFixed(1) }}%</div>
                <div class="stat-change" :class="(stats.processingRate || 0) >= 95 ? 'positive' : 'negative'">
                  <span class="stat-icon">⚡</span>
                  <span>{{ stats.unprocessedCount || 0 }} pending</span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Search Section -->
      <div class="search-section">
        <div class="filter-header">
          <h3 class="filter-title">Filters</h3>
          <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
            <span class="toggle-icon">{{ filtersVisible ? '📁' : '📂' }}</span>
            {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
          </button>
        </div>

        <transition name="slide-fade">
          <div v-if="filtersVisible" class="search-controls-wrapper">
            <div class="search-controls">
              <div class="search-group date-filter-group">
                <label class="search-label">Date Range <span class="date-range-showing">{{ activeDateRangeLabel }}</span></label>
                <div class="date-quick-buttons">
                  <button v-for="opt in dateRangeOptions" :key="opt.value"
                    :class="['date-quick-btn', { active: selectedDateRange === opt.value }]"
                    @click="selectDateRange(opt.value)">{{ opt.label }}</button>
                </div>
              </div>

              <div class="search-group">
                <label class="search-label">Activity Type <span class="multi-badge" v-if="filters.activityTypes.length">({{ filters.activityTypes.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('activityType')">
                    <span v-if="filters.activityTypes.length === 0" class="placeholder">All Activities</span>
                    <span v-else class="selected-items">
                      {{ filters.activityTypes.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.activityType" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllActivityTypes" :checked="filters.activityTypes.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="type in activityTypeOptions" :key="type.value">
                        <input type="checkbox" :value="type.value" v-model="filters.activityTypes" @change="applyFilters">
                        <span>{{ type.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="search-group">
                <label class="search-label">Store <span class="multi-badge" v-if="filters.deviceIds.length">({{ filters.deviceIds.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('device')">
                    <span v-if="filters.deviceIds.length === 0" class="placeholder">All Devices</span>
                    <span v-else class="selected-items">
                      {{ filters.deviceIds.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.device" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllDevices" :checked="filters.deviceIds.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="device in uniqueDevices" :key="device">
                        <input type="checkbox" :value="device" v-model="filters.deviceIds" @change="applyFilters">
                        <span>{{ device }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="search-group">
                <label class="search-label">Status <span class="multi-badge" v-if="filters.statuses.length">({{ filters.statuses.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('status')">
                    <span v-if="filters.statuses.length === 0" class="placeholder">All Status</span>
                    <span v-else class="selected-items">
                      {{ filters.statuses.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.status" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllStatuses" :checked="filters.statuses.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="status in statusOptions" :key="status.value">
                        <input type="checkbox" :value="status.value" v-model="filters.statuses" @change="applyFilters">
                        <span>{{ status.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Column Selector Dropdown -->
              <div class="search-group">
                <label class="search-label">
                  Visible Columns
                  <span class="multi-badge" v-if="selectedColumns.length !== availableColumns.length">
                    ({{ selectedColumns.length }}/{{ availableColumns.length }})
                  </span>
                </label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('columns')">
                    <span v-if="selectedColumns.length === availableColumns.length" class="placeholder">All Columns</span>
                    <span v-else class="selected-items">
                      {{ selectedColumns.length }} columns selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.columns" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllColumns" :checked="selectedColumns.length === availableColumns.length">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="column in availableColumns" :key="column.key">
                        <input type="checkbox" :value="column.key" v-model="selectedColumns" @change="handleColumnVisibilityChange">
                        <span>{{ column.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="filter-actions">
              <button class="apply-filters-btn" @click="applyFilters">
                Apply Filters
              </button>
              <button class="reset-filters-btn" @click="resetFilters">
                Reset
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Data Grid -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">
            IOT Activity Feed
          </div>
          <div class="grid-controls">
          <button
            v-if="hasActiveColumnSearches"
            class="clear-column-searches-btn"
            @click="clearColumnSearches"
          >
            <span class="action-icon">✖</span><span>Clear Searches</span>
          </button>
            <div class="rows-selector">
              <span class="rows-label">Show:</span>
              <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                <option :value="10">10 rows</option>
                <option :value="15">15 rows</option>
                <option :value="20">20 rows</option>
                <option :value="25">25 rows</option>
                <option :value="50">50 rows</option>
                <option :value="100">100 rows</option>
              </select>
            </div>
            <div class="grid-actions">
              <button class="action-button popout-btn" @click="openInNewWindow" title="Pop Out">
                <span class="btn-icon">⬜</span>
                <span>Pop Out</span>
              </button>
              <button class="action-button refresh-btn" @click="refreshData" title="Refresh">
                <span class="btn-icon">🔄</span>
                <span>Refresh</span>
              </button>
              <button class="action-button export-btn" @click="exportData" title="Export">
                <span class="btn-icon">📥</span>
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        <div class="table-container">
          <!-- Loading State -->
          <div v-if="loading" class="loading-overlay">
            <div class="loader"></div>
          </div>

          <!-- Data Table with Dynamic Columns -->
          <table v-if="!loading && filteredActivities.length > 0" class="data-table">
          <thead>
            <tr>
              <th v-for="column in visibleColumns" :key="column.key">
                {{ column.label }}
              </th>
            </tr>
            <!-- NEW: Column search row -->
            <tr class="search-row">
              <th v-for="column in visibleColumns" :key="'s-' + column.key" class="search-cell">
                <input
                  type="text"
                  class="column-search-input"
                  placeholder="Search..."
                  v-model="columnSearchFilters[column.key]"
                  @input="handleColumnSearch"
                />
              </th>
            </tr>
          </thead>
            <tbody>
              <!-- <tr v-for="activity in paginatedActivities" :key="activity.id"> -->
              <tr v-for="activity in paginatedActivities" :key="activity.id" @click="showActivityDetails(activity)" style="cursor: pointer;">
                <!-- Dynamic columns -->
                <td v-for="column in visibleColumns" :key="column.key">
                  <!-- Date/Time columns -->
                  <div v-if="column.key.includes('Date') || column.key.includes('Time')">
                    <div>{{ formatDateTime(activity[column.key]) }}</div>
                    <div style="font-size: 0.75rem; color: #6c757d;" v-if="column.key === 'CreateDate' || column.key === 'EventTime'">
                      {{ getRelativeTime(activity[column.key]) }}
                    </div>
                  </div>
                  
                  <!-- Device info (combo of device ID and alias) -->
                  <div v-else-if="column.key === 'Telemetry_DeviceId'" class="device-info">
                    <span class="device-id">{{ activity[column.key] }}</span>
                    <span class="device-alias">{{ activity.Telemetry_DeviceAlias }}</span>
                  </div>
                  
                  <!-- Volume columns -->
                  <div v-else-if="column.key === 'Vol' || column.key === 'MaxVol'">
                    <span class="volume-display" v-if="activity[column.key]">
                      {{ parseFloat(activity[column.key]).toFixed(2) }}L
                    </span>
                    <span v-else style="color: #6c757d;">-</span>
                  </div>
                  
                  <!-- Boolean/Processed columns -->
                  <div v-else-if="column.key === 'IsProcessed' || column.key === 'AccountEnabled' || column.key === 'StoreEnabled' || column.key === 'IOTDevice_Enable'">
                    {{ activity[column.key] === 1 || activity[column.key] === true ? 'Yes' : 'No' }}
                  </div>
                  
                  <!-- Coordinates (lat/long)-->
                  <div v-else-if="column.key === 'StoreLatitude' || column.key === 'StoreLongitude'">
                    {{ activity[column.key] ? parseFloat(activity[column.key]).toFixed(6) : '-' }}
                  </div>
                  
                  <!-- Message/Text columns (truncated) -->
                  <div v-else-if="column.key === 'Telemetry_Message' || column.key === 'BodyRaw' || column.key === 'TelemetryDataRaw'" class="message-cell">
                    {{ activity[column.key] || '-' }}
                  </div>
                  
                  <!-- Default text columns -->
                  <div v-else>
                    {{ activity[column.key] || '-' }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Empty State -->
          <div v-if="!loading && filteredActivities.length === 0" class="empty-state">
            <div class="empty-icon">📂</div>
            <div class="empty-title">No Activities Found</div>
            <div class="empty-description">
              Try adjusting your filters or check back later for new data
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination-container">
          <div class="pagination-info">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
            {{ Math.min(currentPage * itemsPerPage, filteredActivities.length) }}
            of {{ filteredActivities.length }} entries
            <span class="per-page-info">({{ itemsPerPage }} per page)</span>
          </div>
          <div class="pagination-controls">
            <button
              class="page-btn prev-btn"
              @click="currentPage = 1"
              :disabled="currentPage === 1">
              Previous
            </button>
            <button
              v-for="page in visiblePageNumbers"
              :key="page"
              class="page-btn page-number"
              :class="{ 'active': page === currentPage }"
              @click="currentPage = page">
              {{ page }}
            </button>
            <button
              class="page-btn next-btn"
              @click="currentPage++"
              :disabled="currentPage === totalPages">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FamsIotDashboard',

  data() {
    return {
       // Add these new properties
      showEventsBreakdown: false,
      eventsByDevice: [],
      totalEventsInRange: 0,
      eventsBreakdownTitle: '',
      currentPeriodLabel: '',

      // Add these new properties
      showVolumeBreakdown: false,
      volumeByDevice: [],
      volumeEventCount: 0,

       // Add these new properties
      showDeviceList: false,
      deviceListWithStats: [],

      // Add this new property
      selectedActivity: null,
      // Loader states
      showLoaderOption: true,
      isLoading: false,
      loaderMessage: 'Loading...',
      loaderTimeout: null,
      minLoaderTime: 1500,
      loaderStartTime: null,

      // Auto-refresh settings
      autoRefreshEnabled: false,
      refreshInterval: 30000,
      refreshTimer: null,
      nextRefreshTime: null,

      // Connection and loading states
      connectionStatus: 'Live',
      loading: false,
      lastUpdate: null,
      errorCount: 0,

      // API Configuration
      apiConfig: {
        baseURL: process.env.VUE_APP_API_URL || '',
        accountId: localStorage.getItem('userAccountId'),
        refreshInterval: 15000,
      },

      // Pagination
      currentPage: 1,
      itemsPerPage: 15,

      // Data
      activities: [],
      rawApiData: [],
      uniqueDevices: [],
      uniqueStores: [],

      // Statistics
      stats: {
        totalDevices: 0,
        totalActivities: 0,
        totalVolume: 0,
        totalStores: 0,
        processedCount: 0,
        unprocessedCount: 0,
        processingRate: 95,
        eventsTrend: 12.5,
        avgVolume: 125.5
      },

      // Filters
      filters: {
        startDateTime: this.getDefaultStartDate(), // 3 days ago at 00:00
        endDateTime: this.getDefaultEndDate(),     // Today at 23:59
        activityTypes: [],
        deviceIds: [],
        statuses: [],
        specificDate: ''
      },
      selectedDateRange: 'last3days',
      dateRangeOptions: [
        { value: 'last3days', label: 'Last 3 Days' },
        { value: 'today',     label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: '2daysago',  label: '2 Days Ago' }
      ],
      filtersVisible: true,
      statsVisible: true,

      // Dropdown states for multi-select
      dropdownStates: {
        activityType: false,
        device: false,
        status: false,
        columns: false
      },

      // Filter options
      activityTypeOptions: [
      ],

      statusOptions: [
        { value: 'processed', label: 'Processed' },
        { value: 'pending', label: 'Pending' },
        { value: 'error', label: 'Error' }
      ],

      columnSearchFilters: {},
      // Column management
      availableColumns: [
        // Core Event Fields
        { key: 'id', label: 'Event ID', visible: false },
        { key: 'createDate', label: 'Create Date', visible: true },
        { key: 'eventTime', label: 'Event Time', visible: true },
        { key: 'telemetry_DateTime', label: 'Telemetry DateTime', visible: false },
        { key: 'telemetry_Date', label: 'Telemetry Date', visible: false },
        { key: 'telemetry_Time', label: 'Telemetry Time', visible: false },
        { key: 'time', label: 'Time', visible: false },
        
        // Telemetry Fields
        { key: 'telemetry_DeviceId', label: 'Device ID', visible: true },
        { key: 'telemetry_DeviceAlias', label: 'Device Alias', visible: true },
        { key: 'telemetry_Type', label: 'Event Type Code', visible: false },
        { key: 'telemetry_RecordTypeID', label: 'Record Type ID', visible: false },
        { key: 'telemetry_Message', label: 'Message', visible: true },
        { key: 'fmsEventType', label: 'FMS Event Type', visible: false },
        { key: 'eventType', label: 'Event Type', visible: false },
        
        // Transaction & Volume Data
        { key: 'trId', label: 'Transaction ID', visible: true },
        { key: 'vol', label: 'Volume (L)', visible: true },
        { key: 'maxVol', label: 'Max Volume (L)', visible: false },
        { key: 'prod', label: 'Product', visible: false },
        
        // Vehicle Information
        { key: 'vehReg', label: 'Vehicle Registration', visible: false },
        { key: 'km', label: 'Kilometers', visible: false },
        { key: 'hrs', label: 'Hours', visible: false },
        
        // Account Fields
        { key: 'accountID', label: 'Account ID', visible: false },
        { key: 'account_ID', label: 'Account Primary ID', visible: false },
        { key: 'accountName', label: 'Account Name', visible: true },
        { key: 'accountEnabled', label: 'Account Enabled', visible: false },
        { key: 'accountMonthStartDate', label: 'Account Month Start', visible: false },
        { key: 'accountMonthEndDate', label: 'Account Month End', visible: false },
        { key: 'account_TypeID', label: 'Account Type ID', visible: false },
        { key: 'account_CreatedDate', label: 'Account Created', visible: false },
        { key: 'accountBlocked', label: 'Account Blocked', visible: false },
        { key: 'account_SyncReportCategory', label: 'Account Sync Report Category', visible: false },
        { key: 'account_SyncDashCategory', label: 'Account Sync Dashboard Category', visible: false },
        { key: 'account_Key', label: 'Account Key', visible: false },
        { key: 'account_KeyEncode', label: 'Account Key Encoded', visible: false },
        
        // Store Fields
        { key: 'store_ID', label: 'Store ID', visible: true },
        { key: 'store_AccountID', label: 'Store Account ID', visible: false },
        { key: 'storeName', label: 'Store Name', visible: true },
        { key: 'store_TypeID', label: 'Store Type ID', visible: false },
        { key: 'storeTimeZoneID', label: 'Store Timezone', visible: false },
        { key: 'storeHardwareID', label: 'Store Hardware ID', visible: false },
        { key: 'storeMacAddress', label: 'Store MAC Address', visible: false },
        { key: 'storeIPAddress', label: 'Store IP Address', visible: false },
        { key: 'storeCellNumber', label: 'Store Cell Number', visible: false },
        { key: 'storeEnabled', label: 'Store Enabled', visible: false },
        { key: 'storeLatitude', label: 'Store Latitude', visible: false },
        { key: 'storeLongitude', label: 'Store Longitude', visible: false },
        { key: 'storeCreateDate', label: 'Store Created', visible: false },
        { key: 'storeRenewDate', label: 'Store Renew Date', visible: false },
        { key: 'storeDeviceId', label: 'Store Device ID', visible: false },
        { key: 'storeDeviceETag', label: 'Store Device ETag', visible: false },
        { key: 'storeProvincesID', label: 'Store Province ID', visible: false },
        { key: 'storeIOTEnable', label: 'Store IOT Enabled', visible: false },
        { key: 'storeReProcess', label: 'Store Reprocess', visible: false },
        
        // IOT Device Fields
        { key: 'iotDeviceID', label: 'IOT Device ID', visible: true },
        { key: 'iotDevice_ID', label: 'IOT Device Primary ID', visible: false },
        { key: 'iotDevice_AccountID', label: 'IOT Device Account ID', visible: false },
        { key: 'iotDevice_StoreID', label: 'IOT Device Store ID', visible: false },
        { key: 'iotDevice_DeviceID', label: 'IOT Device Reference ID', visible: false },
        { key: 'iotDevice_DeviceName', label: 'IOT Device Name', visible: false },
        { key: 'iotDevice_Name', label: 'IOT Device Display Name', visible: false },
        { key: 'iotDevice_Description', label: 'IOT Device Description', visible: false },
        { key: 'iotDevice_Enable', label: 'IOT Device Enabled', visible: false },
        { key: 'iotDevice_TypeID', label: 'IOT Device Type ID', visible: false },
        { key: 'iotDevice_TankID', label: 'Tank ID', visible: false },
        { key: 'iotDevice_EquipmentID', label: 'Equipment ID', visible: false },
        { key: 'iotDevice_CreateDate', label: 'IOT Device Created', visible: false },
        { key: 'iotDevice_Etag', label: 'IOT Device ETag', visible: false },
        { key: 'iotDevice_PrimaryKey', label: 'IOT Device Primary Key', visible: false },
        { key: 'iotDevice_SecondaryKey', label: 'IOT Device Secondary Key', visible: false },
        
        // Equipment & Dispensing
        { key: 'eqpTag', label: 'Equipment Tag', visible: false },
        { key: 'telemetry_StartTime', label: 'Start Time', visible: false },
        { key: 'telemetry_EndTime', label: 'End Time', visible: false },
        
        // Verification & Allocation
        { key: 'verifyType', label: 'Verification Type', visible: false },
        { key: 'allocType', label: 'Allocation Type', visible: false },
        
        // Totalizer Data
        { key: 'totaliserStart', label: 'Totaliser Start', visible: false },
        { key: 'totaliserEnd', label: 'Totaliser End', visible: false },
        
        // Authentication & Metadata
        { key: 'authMethod', label: 'Auth Method', visible: false },
        { key: 'authGenerationID', label: 'Auth Generation ID', visible: false },
        { key: 'isProcessed', label: 'Is Processed', visible: false },
        { key: 'topic', label: 'Topic', visible: false },
        { key: 'subject', label: 'Subject', visible: false },
        { key: 'iotID', label: 'IoT ID', visible: false },
        { key: 'enqueueTime', label: 'Enqueue Time', visible: false },
        { key: 'messageSource', label: 'Message Source', visible: false },
        { key: 'dataVersion', label: 'Data Version', visible: false },
        { key: 'metaDataVersion', label: 'Metadata Version', visible: false },
        { key: 'telemetryDataRaw', label: 'Raw Telemetry Data', visible: false },
        { key: 'bodyRaw', label: 'Raw Body', visible: false },
        { key: 'typeID', label: 'Type ID', visible: false },
        { key: 'deviceId', label: 'Device ID (Alt)', visible: false },
        { key: 'deviceAlias', label: 'Device Alias (Alt)', visible: false },
        { key: 'recordTypeID', label: 'Record Type ID (Alt)', visible: false }
      ],

      selectedColumns: ['createDate', 'telemetry_DeviceId', 'telemetry_DeviceAlias', 'storeName', 'telemetry_Type', 'trId', 'vol', 'telemetry_Message', 'isProcessed'],

      pollingInterval: null
    };
  },

  computed: {
    filteredActivities() {
      let filtered = [...this.activities];

      if (this.filters.startDateTime) {
        const startDate = new Date(this.filters.startDateTime);
        filtered = filtered.filter(a => new Date(a.timestamp || a.createDate) >= startDate);
      }
      if (this.filters.endDateTime) {
        const endDate = new Date(this.filters.endDateTime);
        filtered = filtered.filter(a => new Date(a.timestamp || a.createDate) <= endDate);
      }
      if (this.filters.activityTypes.length > 0) {
        filtered = filtered.filter(a => this.filters.activityTypes.includes(a.fmsEventType));
      }
      if (this.filters.deviceIds.length > 0) {
        filtered = filtered.filter(a => this.filters.deviceIds.includes(a.storeName));
      }
      if (this.filters.statuses.length > 0) {
        filtered = filtered.filter(a => this.filters.statuses.includes(a.status));
      }

      // Column-level search filters
      if (Object.keys(this.columnSearchFilters).length > 0) {
        filtered = filtered.filter(activity => {
          for (const key in this.columnSearchFilters) {
            const sv = this.columnSearchFilters[key];
            if (!sv || !sv.trim()) continue;
            const search = sv.toLowerCase();

            let val = '';
            if (key === 'timestamp') {
              val = this.formatDateTime(activity.timestamp || activity.createDate) || '';
            } else if (key === 'deviceId') {
              val = ((activity.deviceId || '') + ' ' + (activity.deviceAlias || ''));
            } else if (key === 'typeCode') {
              val = String(activity.typeCode || activity.typeID || '');
            } else if (key === 'transactionId') {
              val = String(activity.transactionId || activity.transactionID || '');
            } else {
              val = String(activity[key] != null ? activity[key] : '');
            }

            if (!val.toLowerCase().includes(search)) return false;
          }
          return true;
        });
      }

      return filtered;
    },

    totalPages() {
      return Math.ceil(this.filteredActivities.length / this.itemsPerPage);
    },

    paginatedActivities() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredActivities.slice(start, end);
    },

    hasActiveColumnSearches() {
      return Object.keys(this.columnSearchFilters).some(k =>
        this.columnSearchFilters[k] && this.columnSearchFilters[k].trim() !== ''
      );
    },

    visiblePageNumbers() {
      const maxVisible = 5;
      const pages = [];
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    visibleColumns() {
      return this.availableColumns.filter(col => this.selectedColumns.includes(col.key));
    },

    minAllowedDate() {
      const d = new Date();
      d.setDate(d.getDate() - 3);
      return d.toISOString().split('T')[0];
    },

    maxAllowedDate() {
      return new Date().toISOString().split('T')[0];
    },

    activeDateRangeLabel() {
      if (this.filters.specificDate)
        return `Showing: ${this.filters.specificDate} (00:00 – 23:59)`;
      const opt = this.dateRangeOptions.find(o => o.value === this.selectedDateRange);
      return opt ? opt.label : '';
    }
  },

   // ADD A WATCHER FOR FILTERED ACTIVITIES (optional but recommended)
  watch: {
    filteredActivities: {
      handler: function() {
        // Automatically update stats when filtered data changes
        this.updateStats();
      },
      deep: false
      
    }
  },

  methods: {
getStatsDateRangeLabel() {
    if (this.filters.startDateTime && this.filters.endDateTime) {
      const start = new Date(this.filters.startDateTime);
      const end = new Date(this.filters.endDateTime);
      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

      if (daysDiff === 3) {
        return 'Total Events (Last 3 Days)';
      } else if (daysDiff === 1) {
        return 'Total Events (Today)';
      } else if (daysDiff === 7) {
        return 'Total Events (Last Week)';
      } else {
        return `Total Events (${daysDiff} Days)`;
      }
    }
    return 'Total Events';
  },

  handleColumnSearch() {
  this.currentPage = 1;
    },

  clearColumnSearches() {
    this.columnSearchFilters = {};
    this.currentPage = 1;
    },
 // ADD THESE NEW METHODS FOR DEFAULT DATES
    getDefaultStartDate() {
      const date = new Date();
      date.setDate(date.getDate() - 3); // 3 days ago
      date.setHours(0, 0, 0, 0);        // Set to 00:00:00
      // Format for datetime-local input: YYYY-MM-DDTHH:MM
      return this.formatDateForInput(date);
    },

    getDefaultEndDate() {
      const date = new Date();
      date.setHours(23, 59, 59, 999);   // Today at 23:59:59
      // Format for datetime-local input
      return this.formatDateForInput(date);
    },

    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },

    selectDateRange(rangeValue) {
      this.selectedDateRange = rangeValue;
      this.filters.specificDate = '';
      const now = new Date();
      const offsets = { last3days: -3, today: 0, yesterday: -1, '2daysago': -2 };
      const offset = offsets[rangeValue];

      const start = new Date(now);
      start.setDate(start.getDate() + (rangeValue === 'last3days' ? -3 : offset));
      start.setHours(0, 0, 0, 0);

      const end = new Date(now);
      if (rangeValue !== 'last3days') end.setDate(end.getDate() + offset);
      end.setHours(23, 59, 59, 999);

      this.filters.startDateTime = this.formatDateForInput(start);
      this.filters.endDateTime   = this.formatDateForInput(end);
      this.applyFilters();
    },

    onSpecificDateChange() {
      if (!this.filters.specificDate) return;
      this.selectedDateRange = '';
      const [y, m, d] = this.filters.specificDate.split('-');
      const start = new Date(y, m - 1, d, 0, 0, 0, 0);
      const end   = new Date(y, m - 1, d, 23, 59, 59, 999);
      this.filters.startDateTime = this.formatDateForInput(start);
      this.filters.endDateTime   = this.formatDateForInput(end);
      this.applyFilters();
    },
    // Add this new method to open events breakdown
    openEventsBreakdown() {
      // Determine which events to analyze
      const eventsToAnalyze = this.filteredActivities;
      this.totalEventsInRange = eventsToAnalyze.length;

      // Set title based on filters
      if (this.filters.startDateTime || this.filters.endDateTime) {
        this.eventsBreakdownTitle = `${this.totalEventsInRange} Events in Selected Range`;
        const start = this.filters.startDateTime ? new Date(this.filters.startDateTime).toLocaleDateString() : 'Start';
        const end = this.filters.endDateTime ? new Date(this.filters.endDateTime).toLocaleDateString() : 'Now';
        this.currentPeriodLabel = `${start} - ${end}`;
      } else {
        this.eventsBreakdownTitle = `${this.totalEventsInRange} Events Today`;
        this.currentPeriodLabel = new Date().toLocaleDateString();
      }

      // Group events by device
      const deviceEventsMap = {};

      eventsToAnalyze.forEach(activity => {
        const deviceId = activity.deviceId;

        if (!deviceEventsMap[deviceId]) {
          deviceEventsMap[deviceId] = {
            deviceId: deviceId,
            alias: activity.deviceAlias,
            totalEvents: 0,
            eventTypes: {},
            events: [],
            expanded: false,
            firstEvent: activity.timestamp || activity.createDate,
            lastEvent: activity.timestamp || activity.createDate,
            hourCounts: {}
          };
        }

        // Update device stats
        deviceEventsMap[deviceId].totalEvents++;
        deviceEventsMap[deviceId].events.push(activity);

        // Track event types
        const eventType = activity.type;
        if (!deviceEventsMap[deviceId].eventTypes[eventType]) {
          deviceEventsMap[deviceId].eventTypes[eventType] = 0;
        }
        deviceEventsMap[deviceId].eventTypes[eventType]++;

        // Update first/last event times
        const eventTime = new Date(activity.timestamp || activity.createDate);
        if (eventTime < new Date(deviceEventsMap[deviceId].firstEvent)) {
          deviceEventsMap[deviceId].firstEvent = activity.timestamp || activity.createDate;
        }
        if (eventTime > new Date(deviceEventsMap[deviceId].lastEvent)) {
          deviceEventsMap[deviceId].lastEvent = activity.timestamp || activity.createDate;
        }

        // Track hourly distribution
        const hour = eventTime.getHours();
        if (!deviceEventsMap[deviceId].hourCounts[hour]) {
          deviceEventsMap[deviceId].hourCounts[hour] = 0;
        }
        deviceEventsMap[deviceId].hourCounts[hour]++;
      });

      // Convert to array and calculate percentages
      this.eventsByDevice = Object.values(deviceEventsMap)
        .map(device => {
          // Convert event types to array
          const eventTypesArray = Object.entries(device.eventTypes)
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => b.count - a.count);

          // Find peak hour
          let peakHour = 0;
          let maxCount = 0;
          Object.entries(device.hourCounts).forEach(([hour, count]) => {
            if (count > maxCount) {
              maxCount = count;
              peakHour = parseInt(hour);
            }
          });

          return {
            ...device,
            eventTypes: eventTypesArray,
            percentage: this.totalEventsInRange > 0
              ? (device.totalEvents / this.totalEventsInRange) * 100
              : 0,
            peakHour: `${peakHour}:00 - ${peakHour + 1}:00 (${maxCount} events)`
          };
        })
        .sort((a, b) => b.totalEvents - a.totalEvents); // Sort by total events

      this.showEventsBreakdown = true;
    },

// Add this new method to open volume breakdown
    openVolumeBreakdown() {
          // Calculate volume statistics by device
          const deviceVolumeMap = {};
          let totalVolumeEvents = 0;

          // Process filtered activities for volume data
          this.filteredActivities.forEach(activity => {
            if (activity.volume && activity.volume > 0) {
              totalVolumeEvents++;
              const deviceId = activity.deviceId;

              if (!deviceVolumeMap[deviceId]) {
                deviceVolumeMap[deviceId] = {
                  deviceId: deviceId,
                  alias: activity.deviceAlias,
                  totalVolume: 0,
                  eventCount: 0,
                  volumes: [],
                  maxVolume: 0
                };
              }

              deviceVolumeMap[deviceId].totalVolume += activity.volume;
              deviceVolumeMap[deviceId].eventCount++;
              deviceVolumeMap[deviceId].volumes.push(activity.volume);
              deviceVolumeMap[deviceId].maxVolume = Math.max(deviceVolumeMap[deviceId].maxVolume, activity.volume);
            }
          });

          // Convert to array and calculate percentages
          const totalVolume = this.stats.totalVolume || 0;
          this.volumeByDevice = Object.values(deviceVolumeMap)
            .map(device => ({
              ...device,
              avgVolume: device.eventCount > 0 ? device.totalVolume / device.eventCount : 0,
              percentage: totalVolume > 0 ? (device.totalVolume / totalVolume) * 100 : 0
            }))
            .sort((a, b) => b.totalVolume - a.totalVolume); // Sort by total volume

          this.volumeEventCount = totalVolumeEvents;
          this.showVolumeBreakdown = true;
        },
    // Add this new method to open device list
    openDeviceList() {
      // Calculate stats for each device
      this.deviceListWithStats = this.uniqueDevices.map(deviceId => {
        const deviceActivities = this.filteredActivities.filter(a => a.deviceId === deviceId);
        const deviceAlias = deviceActivities.length > 0 ? deviceActivities[0].deviceAlias : '';
        const totalVolume = deviceActivities.reduce((sum, a) => sum + (parseFloat(a.vol) || 0), 0);
        const lastActivity = deviceActivities[0]; // Assuming sorted by date

        return {
          id: deviceId,
          alias: deviceAlias,
          eventCount: deviceActivities.length,
          totalVolume: totalVolume,
          lastStatus: lastActivity ? lastActivity.status : 'unknown',
          lastActivity: lastActivity ? this.formatDateTime(lastActivity.timestamp || lastActivity.createDate) : 'N/A'
        };
      }).sort((a, b) => b.eventCount - a.eventCount); // Sort by event count

      this.showDeviceList = true;
    },


     // Add this new method
    showActivityDetails(activity) {
      this.selectedActivity = activity;
    },

    startLoader(message = 'Loading...') {
      if (this.showLoaderOption) {
        this.loaderMessage = message;
        this.isLoading = true;
        this.loaderStartTime = Date.now();

        if (this.loaderTimeout) {
          clearTimeout(this.loaderTimeout);
        }

        this.loaderTimeout = setTimeout(() => {
          console.warn('Loader timeout reached, hiding loader');
          this.stopLoader(true);
        }, 30000);
      }
    },

    stopLoader(forceStop = false) {
      const elapsedTime = this.loaderStartTime ? Date.now() - this.loaderStartTime : 0;
      const remainingTime = Math.max(0, this.minLoaderTime - elapsedTime);

      if (forceStop || remainingTime === 0) {
        this.isLoading = false;
        this.loaderMessage = 'Loading...';
        this.loaderStartTime = null;

        if (this.loaderTimeout) {
          clearTimeout(this.loaderTimeout);
          this.loaderTimeout = null;
        }
      } else {
        setTimeout(() => {
          this.isLoading = false;
          this.loaderMessage = 'Loading...';
          this.loaderStartTime = null;

          if (this.loaderTimeout) {
            clearTimeout(this.loaderTimeout);
            this.loaderTimeout = null;
          }
        }, remainingTime);
      }
    },

    setMinLoaderTime(milliseconds) {
      this.minLoaderTime = Math.max(0, Math.min(5000, milliseconds));
      console.log(`Minimum loader display time set to ${this.minLoaderTime}ms`);
    },

    handleAutoRefreshToggle() {
      if (this.autoRefreshEnabled) {
        this.startAutoRefresh();
        this.showNotification('Success', `Auto-refresh enabled (every ${this.formatRefreshInterval()})`, 'success');
      } else {
        this.stopAutoRefresh();
        this.showNotification('Info', 'Auto-refresh disabled - use manual refresh button', 'primary');
      }
    },

    handleRefreshIntervalChange() {
      if (this.autoRefreshEnabled) {
        this.stopAutoRefresh();
        this.startAutoRefresh();
        this.showNotification('Success', `Refresh interval changed to ${this.formatRefreshInterval()}`, 'success');
      }
    },

    startAutoRefresh() {
      this.stopAutoRefresh();

      if (this.autoRefreshEnabled) {
        console.log(`Starting auto-refresh every ${this.refreshInterval}ms`);

        this.nextRefreshTime = Date.now() + this.refreshInterval;

        this.refreshTimer = setInterval(() => {
          console.log('Auto-refresh triggered');
          this.loadIotNotifications();
          this.nextRefreshTime = Date.now() + this.refreshInterval;
        }, this.refreshInterval);
      }
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
        this.nextRefreshTime = null;
        console.log('Auto-refresh stopped');
      }
    },

    formatRefreshInterval() {
      const seconds = this.refreshInterval / 1000;
      if (seconds < 60) {
        return `${seconds}s`;
      } else {
        const minutes = seconds / 60;
        return minutes < 60 ? `${minutes}m` : `${minutes/60}h`;
      }
    },

    toggleStatistics() {
      this.statsVisible = !this.statsVisible;
      this.savePreferences();
      this.showNotification('Info', this.statsVisible ? 'Statistics shown' : 'Statistics hidden', 'primary');
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
      this.savePreferences();
      this.showNotification('Info', this.filtersVisible ? 'Filters shown' : 'Filters hidden', 'primary');
    },

    savePreferences() {
      try {
        const preferences = {
          statsVisible: this.statsVisible,
          filtersVisible: this.filtersVisible,
          autoRefreshEnabled: this.autoRefreshEnabled,
          refreshInterval: this.refreshInterval,
          showLoaderOption: this.showLoaderOption,
          minLoaderTime: this.minLoaderTime,
          itemsPerPage: this.itemsPerPage,
          selectedColumns: this.selectedColumns
        };
        localStorage.setItem('fams-iot-preferences', JSON.stringify(preferences));
      } catch (e) {
        console.log('Could not save preferences:', e);
      }
    },

    loadPreferences() {
      try {
        const saved = localStorage.getItem('fams-iot-preferences');
        if (saved) {
          const preferences = JSON.parse(saved);
          this.statsVisible = preferences.statsVisible !== undefined ? preferences.statsVisible : true;
          this.filtersVisible = preferences.filtersVisible !== undefined ? preferences.filtersVisible : true;
          this.autoRefreshEnabled = preferences.autoRefreshEnabled || false;
          this.refreshInterval = preferences.refreshInterval || 30000;
          this.showLoaderOption = preferences.showLoaderOption !== undefined ? preferences.showLoaderOption : true;
          this.minLoaderTime = preferences.minLoaderTime || 1500;
          this.itemsPerPage = preferences.itemsPerPage || 15;
          if (preferences.selectedColumns) {
            this.selectedColumns = preferences.selectedColumns;
          }
        }
      } catch (e) {
        console.log('Could not load preferences:', e);
      }
    },

    toggleDropdown(type) {
      Object.keys(this.dropdownStates).forEach(key => {
        if (key !== type) {
          this.dropdownStates[key] = false;
        }
      });
      this.dropdownStates[type] = !this.dropdownStates[type];
    },

    toggleAllActivityTypes() {
      if (this.filters.activityTypes.length === 0) {
        this.filters.activityTypes = [...this.activityTypeOptions.map(opt => opt.value)];
      } else {
        this.filters.activityTypes = [];
      }
      this.applyFilters();
    },

    toggleAllDevices() {
      if (this.filters.deviceIds.length === 0) {
        this.filters.deviceIds = [...this.uniqueDevices];
      } else {
        this.filters.deviceIds = [];
      }
      this.applyFilters();
    },

    toggleAllStatuses() {
      if (this.filters.statuses.length === 0) {
        this.filters.statuses = [...this.statusOptions.map(opt => opt.value)];
      } else {
        this.filters.statuses = [];
      }
      this.applyFilters();
    },

    isColumnVisible(columnKey) {
      return this.selectedColumns.includes(columnKey);
    },

    toggleAllColumns() {
      if (this.selectedColumns.length === this.availableColumns.length) {
        this.selectedColumns = ['createDate', 'telemetry_DeviceId', 'telemetry_DeviceAlias', 'storeName', 'telemetry_Type', 'trId', 'vol', 'telemetry_Message', 'isProcessed'];
      } else {
        this.selectedColumns = this.availableColumns.map(col => col.key);
      }
      this.savePreferences();
    },

    handleColumnVisibilityChange() {
      this.savePreferences();
      this.showNotification('Success', 'Column visibility updated', 'success');
    },

    formatColumnLabel(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace(/Id$/i, 'ID')
        .replace(/Iot/i, 'IOT');
    },

    handleClickOutside(event) {
      const multiSelectContainers = this.$el.querySelectorAll('.multi-select-container');
      let clickedInside = false;

      multiSelectContainers.forEach(container => {
        if (container.contains(event.target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        Object.keys(this.dropdownStates).forEach(key => {
          this.dropdownStates[key] = false;
        });
      }
    },

    getActivityType(typeCode) {
      const typeMap = {
        3: 'stop',
        8: 'select',
        105: 'limit',
        123: 'action',
        124: 'complete',
        125: 'dispense',
        126: 'driver',
        130: 'odometer',
        205: 'alert',
        402: 'auth',
      };
      return typeMap[typeCode] || 'system';
    },

    extractVolumeFromMessage(message) {
      if (!message) return 0;

      const volumeMatch = message.match(/(\d+(?:\.\d+)?)\s*L/i);
      if (volumeMatch) {
        return parseFloat(volumeMatch[1]);
      }

      const litresMatch = message.match(/(\d+(?:\.\d+)?)\s*litre/i);
      if (litresMatch) {
        return parseFloat(litresMatch[1]);
      }

      return 0;
    },

    generateTransactionId() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < 12; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },

    async loadIotNotifications() {
      var self = this;
      try {
        self.startLoader('Loading IOT data...');
        var start = self.formatDateTime(self.filters.startDateTime);
        var end = self.formatDateTime(self.filters.endDateTime);

        self.loading = true;
        self.connectionStatus = 'Loading...';

        var onSuccess = function (response) {
          var res = response.data;
          if (response.data) {
            self.rawApiData = res;
            self.processApiData(res);
            self.lastUpdate = new Date();
            self.connectionStatus = 'Live';
            self.errorCount = 0;

         // Show success notification with date range info
            if (!self.pollingInterval) {
              const dateRangeText = self.filters.startDateTime && self.filters.endDateTime
                ? ' (Last 3 days)'
                : '';
              self.showNotification('Success', `Loaded ${self.filteredActivities.length} activities${dateRangeText}`, 'success');
            }
          }
          self.stopLoader();
        };

        var onFinally = function (res) {
          console.log('IOT data load attempt finished.');
          self.loading = false;
          self.stopLoader();
        };

        self.$ajaxGet(
          self,
          `VwIotdataNotificationComprehensiveActivity/GetIOTDataFMS?accountId=${self.apiConfig.accountId}&startDate=${start}&endDate=${end}`,
          onSuccess,
          onFinally
        );

      } catch (error) {
        console.error('Error loading IOT notifications:', error);
        this.errorCount++;
        this.connectionStatus = 'Connection Error';

        if (this.errorCount >= 3) {
          this.showNotification('Error', 'Failed to load IOT data. Retrying...', 'danger');
        }
        this.loading = false;
        this.stopLoader();
      }
    },

    processApiData(apiData) {
      console.log('Processing API data:', apiData);

      this.activities = apiData.map(item => ({
        // Core Fields - exact camelCase names from API
        id: item.id,
        createDate: item.createDate,
        eventTime: item.eventTime || item.telemetry_Time,
        telemetry_DateTime: item.telemetry_DateTime,
        telemetry_Date: item.telemetry_Date,
        telemetry_Time: item.telemetry_Time,
        time: item.time,
        
        // Telemetry Fields
        telemetry_DeviceId: item.telemetry_DeviceId,
        telemetry_DeviceAlias: item.telemetry_DeviceAlias,
        telemetry_Type: item.telemetry_Type,
        telemetry_RecordTypeID: item.telemetry_RecordTypeID,
        telemetry_Message: item.telemetry_Message,
        fmsEventType: item.fmsEventType,
        eventType: item.eventType,
        
        // Transaction & Volume Data
        trId: item.trId,
        vol: item.vol,
        maxVol: item.maxVol,
        prod: item.prod,
        
        // Vehicle Information
        vehReg: item.vehReg,
        km: item.km,
        hrs: item.hrs,
        
        // Account Fields
        accountID: item.accountID,
        account_ID: item.account_ID,
        accountName: item.accountName,
        accountEnabled: item.accountEnabled,
        accountMonthStartDate: item.accountMonthStartDate,
        accountMonthEndDate: item.accountMonthEndDate,
        account_TypeID: item.account_TypeID,
        account_CreatedDate: item.account_CreatedDate,
        accountBlocked: item.accountBlocked,
        account_SyncReportCategory: item.account_SyncReportCategory,
        account_SyncDashCategory: item.account_SyncDashCategory,
        account_Key: item.account_Key,
        account_KeyEncode: item.account_KeyEncode,
        
        // Store Fields
        store_ID: item.store_ID,
        store_AccountID: item.store_AccountID,
        storeName: item.storeName,
        store_TypeID: item.store_TypeID,
        storeTimeZoneID: item.storeTimeZoneID,
        storeHardwareID: item.storeHardwareID,
        storeMacAddress: item.storeMacAddress,
        storeIPAddress: item.storeIPAddress,
        storeCellNumber: item.storeCellNumber,
        storeEnabled: item.storeEnabled,
        storeLatitude: item.storeLatitude,
        storeLongitude: item.storeLongitude,
        storeCreateDate: item.storeCreateDate,
        storeRenewDate: item.storeRenewDate,
        storeDeviceId: item.storeDeviceId,
        storeDeviceETag: item.storeDeviceETag,
        storeProvincesID: item.storeProvincesID,
        storeIOTEnable: item.storeIOTEnable,
        storeReProcess: item.storeReProcess,
        
        // IOT Device Fields
        iotDeviceID: item.iotDeviceID,
        iotDevice_ID: item.iotDevice_ID,
        iotDevice_AccountID: item.iotDevice_AccountID,
        iotDevice_StoreID: item.iotDevice_StoreID,
        iotDevice_DeviceID: item.iotDevice_DeviceID,
        iotDevice_DeviceName: item.iotDevice_DeviceName,
        iotDevice_Name: item.iotDevice_Name,
        iotDevice_Description: item.iotDevice_Description,
        iotDevice_Enable: item.iotDevice_Enable,
        iotDevice_TypeID: item.iotDevice_TypeID,
        iotDevice_TankID: item.iotDevice_TankID,
        iotDevice_EquipmentID: item.iotDevice_EquipmentID,
        iotDevice_CreateDate: item.iotDevice_CreateDate,
        iotDevice_Etag: item.iotDevice_Etag,
        iotDevice_PrimaryKey: item.iotDevice_PrimaryKey,
        iotDevice_SecondaryKey: item.iotDevice_SecondaryKey,
        
        // Equipment & Dispensing
        eqpTag: item.eqpTag,
        telemetry_StartTime: item.telemetry_StartTime ? (typeof item.telemetry_StartTime === 'number' ? item.telemetry_StartTime : parseInt(item.telemetry_StartTime, 10) || 0) : 0,
        telemetry_EndTime: item.telemetry_EndTime ? (typeof item.telemetry_EndTime === 'number' ? item.telemetry_EndTime : parseInt(item.telemetry_EndTime, 10) || 0) : 0,
        
        // Verification & Allocation
        verifyType: item.verifyType,
        allocType: item.allocType,
        
        // Totalizer Data
        totaliserStart: item.totaliserStart,
        totaliserEnd: item.totaliserEnd,
        
        // Authentication & Metadata
        authMethod: item.authMethod,
        authGenerationID: item.authGenerationID,
        isProcessed: item.isProcessed,
        topic: item.topic,
        subject: item.subject,
        iotID: item.iotID,
        enqueueTime: item.enqueueTime,
        messageSource: item.messageSource,
        dataVersion: item.dataVersion,
        metaDataVersion: item.metaDataVersion,
        telemetryDataRaw: item.telemetryDataRaw,
        bodyRaw: item.bodyRaw,
        typeID: item.typeID,
        
        // Legacy fields for backward compatibility
        deviceId: item.telemetry_DeviceId || item.deviceId,
        deviceAlias: item.telemetry_DeviceAlias || item.deviceAlias,
        recordTypeID: item.telemetry_RecordTypeID || item.recordTypeID,
        timestamp: item.time || new Date(item.createDate).getTime(),
        type: this.getActivityType(item.telemetry_Type || item.typeID),
        volume: item.vol ? parseFloat(item.vol) : 0,
        message: item.telemetry_Message || 'No message',
        status: this.determineStatus(item.telemetry_Type || item.typeID, item.isProcessed)
      }));

      console.log('Total activities processed:', this.activities.length);
      if (this.activities.length > 0) {
        console.log('Sample activity:', this.activities[0]);
      }

      this.activities.sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

      this.updateFilterOptions();
      this.updateStats();
    },

    parseTransactionId(message) {
      if (!message) return null;
      const match = message.match(/TrId[:\s]+([A-Za-z0-9]+)/);
      return match ? match[1] : null;
    },

    determineStatus(typeCode, isProcessed) {
      if (isProcessed === 1) return 'processed';
      if (typeCode === 124) return 'processed';
      if (typeCode === 125) return 'pending';
      if (typeCode === 205) return 'error';
      return 'processed';
    },

    updateFilterOptions() {
      const devices = [...new Set(this.activities.map(a => a.telemetry_DeviceId || a.deviceId).filter(Boolean))];
      this.uniqueDevices = devices;

      const stores = [...new Set(this.activities.map(a => a.store_ID).filter(Boolean))];
      this.uniqueStores = stores;

      // ADD THIS: rebuild activity type options from actual fmsEventType values in the data
      const fmsTypes = [...new Set(
        this.activities.map(a => (a.fmsEventType || '').trim()).filter(Boolean)
      )].sort();
      this.activityTypeOptions = fmsTypes.map(type => ({ value: type, label: type }));
    },

    updateStatsOld() {
      this.stats.totalDevices = this.uniqueDevices.length;
      this.stats.totalStores = this.uniqueStores.length;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayActivities = this.activities.filter(a =>
        new Date(a.createDate || a.timestamp) >= today
      );
      this.stats.totalActivities = todayActivities.length;

      const volumeSum = this.activities.reduce((sum, a) => sum + (parseFloat(a.vol) || 0), 0);
      this.stats.totalVolume = volumeSum;

      const volumeActivities = this.activities.filter(a => a.vol && a.vol > 0);
      this.stats.avgVolume = volumeActivities.length > 0
        ? (volumeSum / volumeActivities.length)
        : 125.5;

      this.stats.processedCount = this.activities.filter(a => a.status === 'processed').length;
      this.stats.unprocessedCount = this.activities.filter(a => a.status === 'pending').length;

      this.stats.processingRate = this.activities.length > 0
        ? ((this.stats.processedCount / this.activities.length) * 100)
        : 98.5;

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      const yesterdayEnd = new Date(yesterday);
      yesterdayEnd.setHours(23, 59, 59, 999);

      const yesterdayActivities = this.activities.filter(a => {
        const activityDate = new Date(a.createDate || a.timestamp);
        return activityDate >= yesterday && activityDate <= yesterdayEnd;
      });

      if (yesterdayActivities.length > 0) {
        const trendPercent = ((todayActivities.length - yesterdayActivities.length) / yesterdayActivities.length) * 100;
        this.stats.eventsTrend = trendPercent;
      } else {
        this.stats.eventsTrend = todayActivities.length > 10 ? 12.5 : -5.2;
      }
    },

    updateStatsRev1() {
          // Use filtered activities instead of all activities
          const dataToAnalyze = this.filteredActivities;

          // Get unique devices and stores from filtered data
          const uniqueFilteredDevices = [...new Set(dataToAnalyze.map(a => a.deviceId).filter(Boolean))];
          const uniqueFilteredStores = [...new Set(dataToAnalyze.map(a => a.storeID).filter(Boolean))];

          // Update counts based on filtered data
          this.stats.totalDevices = uniqueFilteredDevices.length;
          this.stats.totalStores = uniqueFilteredStores.length;

          // Calculate date range for filtered data
          let todayActivities = [];
          let yesterdayActivities = [];

          if (this.filters.startDateTime || this.filters.endDateTime) {
            // When date filters are active, show total in range instead of "today"
            this.stats.totalActivities = dataToAnalyze.length;

            // For trend calculation when filters are active
            if (dataToAnalyze.length > 0) {
              // Sort by date
              const sortedActivities = [...dataToAnalyze].sort((a, b) =>
                new Date(a.createDate || a.timestamp) - new Date(b.createDate || b.timestamp)
              );

              // Get midpoint to compare first half vs second half
              const midpoint = Math.floor(sortedActivities.length / 2);
              const firstHalf = sortedActivities.slice(0, midpoint);
              const secondHalf = sortedActivities.slice(midpoint);

              // Calculate trend based on first half vs second half
              if (firstHalf.length > 0) {
                const trendPercent = ((secondHalf.length - firstHalf.length) / firstHalf.length) * 100;
                this.stats.eventsTrend = trendPercent;
              } else {
                this.stats.eventsTrend = 0;
              }
            } else {
              this.stats.eventsTrend = 0;
            }
          } else {
            // No date filters - use today's data as before
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            todayActivities = dataToAnalyze.filter(a =>
              new Date(a.createDate || a.timestamp) >= today
            );
            this.stats.totalActivities = todayActivities.length;

            // Yesterday's data for comparison
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            const yesterdayEnd = new Date(yesterday);
            yesterdayEnd.setHours(23, 59, 59, 999);

            yesterdayActivities = this.activities.filter(a => {
              const activityDate = new Date(a.createDate || a.timestamp);
              return activityDate >= yesterday && activityDate <= yesterdayEnd;
            });

            // Calculate trend
            if (yesterdayActivities.length > 0) {
              const trendPercent = ((todayActivities.length - yesterdayActivities.length) / yesterdayActivities.length) * 100;
              this.stats.eventsTrend = trendPercent;
            } else {
              this.stats.eventsTrend = todayActivities.length > 10 ? 12.5 : -5.2;
            }
          }

          // Volume calculations from filtered data
          const volumeSum = dataToAnalyze.reduce((sum, a) => sum + (parseFloat(a.vol) || 0), 0);
          this.stats.totalVolume = volumeSum;

          // Average volume from filtered data
          const volumeActivities = dataToAnalyze.filter(a => a.vol && a.vol > 0);
          this.stats.avgVolume = volumeActivities.length > 0
            ? (volumeSum / volumeActivities.length)
            : 0;

          // Processing status from filtered data
          this.stats.processedCount = dataToAnalyze.filter(a => a.status === 'processed').length;
          this.stats.unprocessedCount = dataToAnalyze.filter(a => a.status === 'pending').length;

          // Processing rate from filtered data
          this.stats.processingRate = dataToAnalyze.length > 0
            ? ((this.stats.processedCount / dataToAnalyze.length) * 100)
            : 0;
        },
updateStats() {
      // Use filtered activities which now respects the default date range
      const dataToAnalyze = this.filteredActivities;

      // Get unique devices and stores from filtered data
      const uniqueFilteredDevices = [...new Set(dataToAnalyze.map(a => a.deviceId).filter(Boolean))];
      const uniqueFilteredStores = [...new Set(dataToAnalyze.map(a => a.storeID).filter(Boolean))];

      // Update counts based on filtered data
      this.stats.totalDevices = uniqueFilteredDevices.length;
      this.stats.totalStores = uniqueFilteredStores.length;

      // Since we always have date filters now, show total in range
      this.stats.totalActivities = dataToAnalyze.length;

      // Calculate trend for the date range
      if (dataToAnalyze.length > 0) {
        // Sort by date
        const sortedActivities = [...dataToAnalyze].sort((a, b) =>
          new Date(a.createDate || a.timestamp) - new Date(b.createDate || b.timestamp)
        );

        // Get midpoint to compare first half vs second half
        const midpoint = Math.floor(sortedActivities.length / 2);
        const firstHalf = sortedActivities.slice(0, midpoint);
        const secondHalf = sortedActivities.slice(midpoint);

        // Calculate trend based on first half vs second half
        if (firstHalf.length > 0) {
          const trendPercent = ((secondHalf.length - firstHalf.length) / firstHalf.length) * 100;
          this.stats.eventsTrend = trendPercent;
        } else {
          this.stats.eventsTrend = 0;
        }
      } else {
        this.stats.eventsTrend = 0;
      }

      // Volume calculations from filtered data
      const volumeSum = dataToAnalyze.reduce((sum, a) => sum + (parseFloat(a.vol) || 0), 0);
      this.stats.totalVolume = volumeSum;

      // Average volume from filtered data
      const volumeActivities = dataToAnalyze.filter(a => a.vol && a.vol > 0);
      this.stats.avgVolume = volumeActivities.length > 0
        ? (volumeSum / volumeActivities.length)
        : 0;

      // Processing status from filtered data
      this.stats.processedCount = dataToAnalyze.filter(a => a.status === 'processed').length;
      this.stats.unprocessedCount = dataToAnalyze.filter(a => a.status === 'pending').length;

      // Processing rate from filtered data
      this.stats.processingRate = dataToAnalyze.length > 0
        ? ((this.stats.processedCount / dataToAnalyze.length) * 100)
        : 0;
    },

    formatDateTime(date) {
      const d = new Date(date);

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');

      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    },

    getRelativeTime(date) {
      const seconds = Math.floor((new Date() - new Date(date)) / 1000);

      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return Math.floor(seconds / 60) + ' minutes ago';
      if (seconds < 86400) return Math.floor(seconds / 3600) + ' hours ago';
      if (seconds < 604800) return Math.floor(seconds / 86400) + ' days ago';

      return new Date(date).toLocaleDateString();
    },

    showNotification(title, text, type = 'primary') {
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({
          title: title,
          text: text,
          color: type,
          position: 'top-right'
        });
      } else {
        console.log(`${title}: ${text}`);
      }
    },

    applyFiltersOld() {
      this.startLoader('Applying filters...');
      this.currentPage = 1;

      setTimeout(() => {
        this.showNotification('Success', 'Filters applied', 'success');
        this.stopLoader();
      }, 500);
    },

     applyFilters() {
      this.startLoader('Applying filters...');
      this.currentPage = 1;

      // Update statistics based on filtered data
      this.updateStats();

      // Update the stat card label based on filter state
      if (this.filters.startDateTime || this.filters.endDateTime) {
        // You can optionally update the label dynamically
        // This would require making the label reactive
      }

      setTimeout(() => {
        this.showNotification('Success', 'Filters applied', 'success');
        this.stopLoader();
      }, 500);
    },

    resetFilters() {
      this.startLoader('Resetting filters...');

      this.columnSearchFilters = {};

      this.filters = {
        startDateTime: this.getDefaultStartDate(), // Reset to 3 days ago
        endDateTime: this.getDefaultEndDate(),     // Reset to today 23:59
        activityTypes: [],
        deviceIds: [],
        statuses: [],
        specificDate: ''
      };
      this.selectedDateRange = 'last3days';

      Object.keys(this.dropdownStates).forEach(key => {
        if (key !== 'columns') {
          this.dropdownStates[key] = false;
        }
      });

      this.currentPage = 1;
       // Recalculate statistics for all data
      this.updateStats();

      setTimeout(() => {
        this.showNotification('Success', 'Filters reset to last 3 days', 'success');
        this.stopLoader();
      }, 300);
    },

    async refreshData() {
      this.startLoader('Refreshing data...');
      this.showNotification('Info', 'Refreshing data...', 'primary');

      try {
        await this.loadIotNotifications();
      } catch (error) {
        console.error('Error refreshing data:', error);
        this.stopLoader();
      }
    },

    exportData() {
      this.startLoader('Exporting data...');
      this.showNotification('Info', 'Exporting data...', 'primary');

      setTimeout(() => {
        const headers = this.visibleColumns.map(col => col.label);

        const csvRows = this.filteredActivities.map(activity => {
          return this.visibleColumns.map(col => {
            let value = '';
            switch(col.key) {
              case 'timestamp':
                value = this.formatDateTime(activity.timestamp || activity.createDate);
                break;
              case 'deviceId':
                value = activity.deviceId;
                break;
              case 'volume':
                value = activity.volume || '';
                break;
              case 'message':
                value = `"${activity.message || ''}"`;
                break;
              default:
                value = activity[col.key] || '';
            }
            return value;
          }).join(',');
        });

        const csvContent = [headers.join(','), ...csvRows].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fams_iot_data_${new Date().getTime()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        this.showNotification('Success', 'Data exported successfully', 'success');
        this.stopLoader();
      }, 1000);
    },

    startPolling() {
      console.warn('startPolling is deprecated. Use auto-refresh toggle instead.');
    },

    stopPolling() {
      console.warn('stopPolling is deprecated. Use auto-refresh toggle instead.');
    },

    handleRowsChange() {
      this.currentPage = 1;
      this.showNotification('Success', `Showing ${this.itemsPerPage} rows per page`, 'success');
    },

    openInNewWindow() {
      this.startLoader('Opening in new window...');

      const columnHeaders = this.visibleColumns.map(col => `<th>${col.label}</th>`).join('');

      const tableRows = this.filteredActivities.map(activity => {
        const cells = this.visibleColumns.map(col => {
          let cellContent = '';
          switch(col.key) {
            case 'timestamp':
              cellContent = this.formatDateTime(activity.timestamp || activity.createDate);
              break;
            case 'deviceId':
              cellContent = `${activity.deviceId}<br><small style="color: #999;">${activity.deviceAlias || ''}</small>`;
              break;
            case 'typeCode':
              cellContent = `<span style="font-family: monospace; color: #D2691E;">${activity.typeCode || activity.typeID}</span>`;
              break;
            case 'type':
              cellContent = `<span class="badge badge-${activity.type}">${activity.type}</span>`;
              break;
            case 'transactionId':
              cellContent = `<span style="font-family: monospace; font-size: 11px;">${activity.transactionId || activity.transactionID}</span>`;
              break;
            case 'volume':
              cellContent = activity.volume ? activity.volume.toFixed(2) : '-';
              break;
            case 'message':
              cellContent = activity.message || '-';
              break;
            case 'status':
              cellContent = `<span class="status-${activity.status}">${activity.status}</span>`;
              break;
            case 'isProcessed':
              cellContent = activity.isProcessed === 1 ? 'Yes' : 'No';
              break;
            default:
              cellContent = activity[col.key] || '-';
          }
          return `<td>${cellContent}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('');

      const popupHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>FAMS IOT Activity Feed</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: #f5f5f5;
            }
            .header {
              background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
              color: white;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            h1 { margin: 0; font-size: 24px; }
            .info { margin-top: 10px; font-size: 14px; opacity: 0.9; }
            .container {
              background: white;
              border-radius: 8px;
              padding: 20px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              overflow-x: auto;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              padding: 10px;
              text-align: left;
              border-bottom: 1px solid #e0e0e0;
            }
            th {
              background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
              color: white;
              font-weight: 600;
              position: sticky;
              top: 0;
            }
            tr:hover { background: #f8f9fa; }
            .badge {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 600;
            }
            .badge-dispense { background: #3498DB; color: white; }
            .badge-complete { background: #27AE60; color: white; }
            .badge-action { background: #9B59B6; color: white; }
            .badge-stop { background: #E74C3C; color: white; }
            .badge-limit { background: #E67E22; color: white; }
            .badge-auth { background: #3498DB; color: white; }
            .badge-select { background: #95A5A6; color: white; }
            .badge-odometer { background: #16A085; color: white; }
            .badge-driver { background: #8E44AD; color: white; }
            .badge-alert { background: #C0392B; color: white; }
            .badge-system { background: #34495E; color: white; }
            .status-processed { color: #27AE60; }
            .status-pending { color: #F39C12; }
            .status-error { color: #E74C3C; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>FAMS IOT Activity Feed</h1>
            <div class="info">
              <div>Generated: ${new Date().toLocaleString()}</div>
              <div>Total Records: ${this.filteredActivities.length}</div>
              <div>Last Update: ${this.lastUpdate ? new Date(this.lastUpdate).toLocaleString() : 'N/A'}</div>
            </div>
          </div>
          <div class="container">
            <table>
              <thead>
                <tr>${columnHeaders}</tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>
          </div>
        </body>
        </html>
      `;

      const popupWindow = window.open('', 'FAMS_IOT_Activities',
        'width=1400,height=800,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=yes');

      if (popupWindow) {
        popupWindow.document.write(popupHTML);
        popupWindow.document.close();
        this.showNotification('Success', 'Opened in new window', 'success');
      } else {
        this.showNotification('Warning', 'Please allow pop-ups for this site', 'warning');
      }

      this.stopLoader();
    },

    simulateRealTimeUpdate() {
      setInterval(() => {
        const random = Math.random();
        if (random > 0.95) {
          this.connectionStatus = 'Updating...';
          setTimeout(() => {
            this.connectionStatus = 'Live';
          }, 1000);
        }
      }, 10000);
    }
  },

  mounted() {
    this.loadPreferences();
    // IMPORTANT: Set default dates if not already set by preferences
    if (!this.filters.startDateTime) {
      this.filters.startDateTime = this.getDefaultStartDate();
    }
    if (!this.filters.endDateTime) {
      this.filters.endDateTime = this.getDefaultEndDate();
    }
    this.loadIotNotifications();

    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }

    this.simulateRealTimeUpdate();

    document.addEventListener('click', this.handleClickOutside);

    setTimeout(() => {
      const refreshStatus = this.autoRefreshEnabled
        ? `Auto-refresh enabled (every ${this.formatRefreshInterval()})`
        : 'Auto-refresh is disabled by default';
        this.showNotification('Welcome!', `FAMS Device Activity Dashboard - Showing last 3 days - ${refreshStatus}`, 'success');
    }, 500);
  },

  beforeDestroy() {
    this.stopAutoRefresh();

    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = null;
    }

    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style>
/* All your existing styles plus the new message cell style */
:root {
    --autumn-primary: #D2691E;
    --autumn-accent: #FF8C00;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #e2e8f0;
    height: 100vh;
    overflow: hidden;
    width: 100%;
}

#app {
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-width: none;
}

/* NEW: Message cell styling to show full content */
.message-cell {
  min-width: 250px;
  max-width: 500px;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
  padding: 4px 0;
}

/* Rest of your existing styles... */
.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: var(--autumn-primary);
}

.dashboard-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.loader-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.loader-toggle {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: white;
}

.toggle-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.loader-speed {
  display: flex;
  align-items: center;
}

.speed-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.speed-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.speed-select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.speed-select:hover {
  background: rgba(255, 255, 255, 0.3);
}

.speed-select:focus {
  outline: none;
  border-color: white;
}

.speed-select option {
  background: #D2691E;
  color: white;
}

.auto-refresh-toggle {
  display: flex;
  align-items: center;
  padding-left: 15px;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
}

.refresh-interval {
  display: flex;
  align-items: center;
}

.interval-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.interval-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.interval-select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.interval-select:hover {
  background: rgba(255, 255, 255, 0.3);
}

.interval-select:focus {
  outline: none;
  border-color: white;
}

.interval-select option {
  background: #D2691E;
  color: white;
}

.live-dot.auto-refresh {
  background: #4CAF50;
  animation: pulse 1s infinite;
}

.global-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.global-loader-content {
  background: white;
  border-radius: 16px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.global-loader {
  width: 60px;
  height: 60px;
  border: 4px solid #FFE4B5;
  border-top: 4px solid #D2691E;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loader-text {
  color: #34495E;
  font-size: 1rem;
  font-weight: 500;
  margin-top: 10px;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-dot.error {
  background: #f44336;
  animation: pulse-error 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

@keyframes pulse-error {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}

.main-content {
    flex: 1;
    overflow: auto;
    padding: 20px;
    width: 100%;
    background: #e2e8f0;
}

.stats-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.stats-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.stats-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.toggle-stats-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for depth */
}

.toggle-stats-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(210, 105, 30, 0.3);
}

.stats-wrapper {
  /* padding: 16px; */
  padding: 16px 20px; /* Increased horizontal padding */
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
   background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid #D2691E;
  border: 1px solid #e2e8f0; /* Match main background for subtle border */
  position: relative;
  z-index: 1;
  overflow: visible;
}

.stat-card * {
  position: relative;
  z-index: 2;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  background: #ffffff; /* Pure white on hover */
}

.stat-label {
  color: #2c3e50 !important;
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 600;
  position: relative;
  z-index: 2;
  display: block;
  line-height: 1.2;
}

.stat-value {
  font-size: 1.75rem;
  color: #2c3e50 !important;
  font-weight: bold;
  background: linear-gradient(135deg, #FFFFFF 0%, #FF8C00 100%);
  -webkit-background-clip: text;
  background-clip: text;
}

.stat-change {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change .stat-icon {
  font-size: 0.875rem;
}

.stat-change.positive {
  color: #27AE60;
}

.stat-change.negative {
  color: #E74C3C;
}

.search-section {
  background: #ffffff; /* White card */
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: visible; /* Allow dropdowns to show */
  width: 100%;
  border: 1px solid #cbd5e1;
}

/* Add a higher z-index to the entire search section when a dropdown is open */
.search-section:has(.multi-select-dropdown) {
  z-index: 100;
  position: relative;
}

.search-section.dropdown-open {
  z-index: 100;
  position: relative;
}

.multi-select-container {
   position: relative;
  overflow: visible;
}

.multi-select-display {
  padding: 10px 12px;
  border: 1px solid #94a3b8; /* Darker border */
  border-radius: 8px;
  background: #ffffff; /* Pure white */
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  min-height: 44px;
}

.multi-select-display:hover {
  border-color: #D2691E;
  background: #fff8f3; /* Slight warm tint on hover */
}

.multi-select-display .placeholder {
  color: #999;
}

.multi-select-display .selected-items {
  color: #495057;
  font-weight: 500;
}

.dropdown-arrow {
  color: #999;
  font-size: 0.75rem;
  transition: transform 0.3s ease;
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #D2691E;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 9999; /* Very high z-index to ensure it appears above everything */
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.select-all-option {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8f9fa;
}

.dropdown-options {
  max-height: 200px;
  overflow-y: auto;
}

.checkbox-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.checkbox-option:hover {
  background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #D2691E;
}

.checkbox-option span {
  font-size: 0.875rem;
  color: #495057;
  user-select: none;
}

.multi-badge {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 4px;
}

.dropdown-options::-webkit-scrollbar {
  width: 6px;
}

.dropdown-options::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dropdown-options::-webkit-scrollbar-thumb {
  background: #D2691E;
  border-radius: 3px;
}

.dropdown-options::-webkit-scrollbar-thumb:hover {
  background: #8B4513;
}

.filter-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0; /* Maintain rounded top corners */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.toggle-filter-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for depth */
}

.toggle-filter-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(210, 105, 30, 0.3);
}

.toggle-icon {
  font-size: 1rem;
}

.search-controls-wrapper {
  padding: 16px 20px;
  position: relative;
  overflow: visible;
}

.search-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  overflow: visible;
  position: relative;
}

.search-group {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
}

.search-label {
  font-size: 0.875rem;
  color: #34495E;
  margin-bottom: 4px;
  font-weight: 500;
}

.search-input, .search-select {
  padding: 10px 12px;
  border: 1px solid #94a3b8; /* Darker border */
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #ffffff; /* Pure white */
}

.search-input:focus, .search-select:focus {
   outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.15);
  background: #ffffff;
}

.filter-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  position: relative;
  z-index: 1;
}

.apply-filters-btn {
  padding: 10px 24px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.apply-filters-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(210, 105, 30, 0.3);
}

.reset-filters-btn {
  padding: 10px 24px;
  background: white;
  color: #34495E;
  border: 2px solid #FFE4B5;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.reset-filters-btn:hover {
  background: #FFE4B5;
}

.slide-fade-enter-active {
    transition: all 0.3s ease;
  overflow: visible; /* Allow overflow during animation */
}

.slide-fade-leave-active {
    transition: all 0.3s ease;
  overflow: visible; /* Allow overflow during animation */
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.grid-container {
  background: #ffffff; /* White card */
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden; /* Keep hidden for table scrolling */
  width: 100%;
  border: 1px solid #cbd5e1;
}

.grid-header {
  padding: 16px 20px;
  background: #f1f5f9; /* Medium grey */
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0; /* Maintain rounded top corners */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grid-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
}

.grid-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.rows-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rows-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

.rows-select {
   padding: 6px 12px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
}

.rows-select:hover {
  border-color: #D2691E;
}

.rows-select:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.1);
}

.grid-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 8px 14px;
  background: white;
  border: 1px solid #94a3b8; /* Darker border */
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.action-button:hover {
 background: #f8fafc;
  border-color: #D2691E;
  color: #D2691E;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.action-button.popout-btn:hover {
  background: #e3f2fd;
  border-color: #2196F3;
  color: #2196F3;
}

.action-button.refresh-btn:hover {
  background: #e8f5e9;
  border-color: #4CAF50;
  color: #4CAF50;
}

.action-button.export-btn:hover {
  background: #fff3e0;
  border-color: #FF9800;
  color: #FF9800;
}

.btn-icon {
  font-size: 1rem;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-container {
  overflow: auto;
  position: relative;
  max-height: 500px;
  width: 100%;
  background: #ffffff; /* White background for table area */
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  z-index: 10;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.data-table tbody tr {
   border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: #ffffff;
}

.data-table tbody tr:hover {
   background: #f1f5f9; /* Darker grey on hover */
  cursor: pointer;
}

/* Alternating row colors with more contrast */
.data-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.data-table tbody tr:nth-child(even):hover {
  background: #f1f5f9;
}

.data-table td {
  padding: 12px 16px;
  font-size: 0.875rem;
  color: #34495E;
}

.activity-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-dispense {
  background: linear-gradient(135deg, #3498DB, #2980B9);
  color: white;
}

.badge-complete {
  background: linear-gradient(135deg, #27AE60, #229954);
  color: white;
}

.badge-action {
  background: linear-gradient(135deg, #9B59B6, #8E44AD);
  color: white;
}

.badge-stop {
  background: linear-gradient(135deg, #E74C3C, #C0392B);
  color: white;
}

.badge-limit {
  background: linear-gradient(135deg, #E67E22, #D35400);
  color: white;
}

.badge-auth {
  background: linear-gradient(135deg, #3498DB, #2E86AB);
  color: white;
}

.badge-select {
  background: linear-gradient(135deg, #95A5A6, #7F8C8D);
  color: white;
}

.badge-odometer {
  background: linear-gradient(135deg, #16A085, #138D75);
  color: white;
}

.badge-driver {
  background: linear-gradient(135deg, #8E44AD, #6C3483);
  color: white;
}

.badge-alert {
  background: linear-gradient(135deg, #C0392B, #A93226);
  color: white;
}

.badge-system {
  background: linear-gradient(135deg, #34495E, #2C3E50);
  color: white;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-processed {
  background: rgba(39, 174, 96, 0.1);
  color: #27AE60;
}

.status-pending {
  background: rgba(243, 156, 18, 0.1);
  color: #F39C12;
}

.status-error {
  background: rgba(231, 76, 60, 0.1);
  color: #E74C3C;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.device-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.device-id {
  font-weight: 600;
  color: #34495E;
}

.device-alias {
  font-size: 0.75rem;
  color: #3498DB;
}

.volume-display {
  font-weight: 600;
  color: var(--autumn-primary);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #34495E;
  background: #f8fafc; /* Light grey for empty state */
  border-radius: 8px;
  margin: 20px;
  border: 1px solid #e2e8f0;
}

.empty-icon {
  font-size: 4rem;
  color: var(--autumn-light);
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-description {
  color: #6c757d;
  text-align: center;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(241, 245, 249, 0.95); /* Darker grey overlay */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid var(--autumn-light);
  border-top: 4px solid var(--autumn-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

code {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

.pagination-container {
  padding: 16px 20px;
  border-top: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f1f5f9; /* Medium grey */
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
}

.per-page-info {
  color: #D2691E;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  gap: 4px;
}

.page-btn {
  padding: 6px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #495057;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  min-width: 40px;
}

.page-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #D2691E;
  color: #D2691E;
}

.page-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prev-btn, .next-btn {
  min-width: 70px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }

  .stats-container {
    grid-template-columns: 1fr 1fr;
  }

  .search-controls {
    grid-template-columns: 1fr;
  }

  .data-table {
    font-size: 0.75rem;
  }

  .data-table th, .data-table td {
    padding: 8px;
  }

  .activity-badge {
    font-size: 0.625rem;
    padding: 2px 6px;
  }

  .header-controls {
    flex-wrap: wrap;
  }

  .grid-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .grid-actions {
    justify-content: space-between;
  }

  /* Adjust message cell for mobile */
  .message-cell {
    min-width: 150px;
    max-width: 250px;
    font-size: 0.75rem;
  }
}

/* Additional responsive adjustments for mobile */
@media (max-width: 480px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
  }

  .loader-controls {
    flex-wrap: wrap;
    gap: 10px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .pagination-container {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
  }

  .page-btn {
    min-width: 30px;
    padding: 4px 8px;
    font-size: 0.75rem;
  }

  /* Further adjust message cell for small screens */
  .message-cell {
    min-width: 100px;
    max-width: 200px;
  }
}

/* Large screens optimization for message cell */
@media (min-width: 1400px) {
  .message-cell {
    max-width: 600px;
  }
}

/* Simple Popup Styles */
.simple-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-radius: 8px 8px 0 0;
}

.popup-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.popup-body {
  padding: 20px;
}

.detail-row {
  margin-bottom: 12px;
  font-size: 0.9rem;
}

.detail-row strong {
  color: #34495E;
  display: inline-block;
  min-width: 120px;
}

.message-text {
  margin-top: 8px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  line-height: 1.5;
  word-wrap: break-word;
}

/* Make table rows hover effect more obvious */
.data-table tbody tr:hover {
  background: rgba(210, 105, 30, 0.1);
  cursor: pointer;
}

@media (min-width: 2000px) {
  .header-content {
    max-width: 1920px;
    margin: 0 auto;
  }

  .main-content {
    max-width: 1920px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }

  .header-content {
    padding: 0 12px;
  }

  .stats-header,
  .filter-header,
  .grid-header,
  .stats-wrapper,
  .search-controls-wrapper,
  .pagination-container {
    padding-left: 12px;
    padding-right: 12px;
  }
}

/* Clickable stat card */
.clickable-stat {
  cursor: pointer;
  transition: all 0.3s ease;
}

.clickable-stat:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  background: #ffffff !important;
  border-color: #D2691E;
}

/* Device list container */
.device-list-container {
  max-height: 400px;
  overflow-y: auto;
}

/* Device list item */
.device-list-item {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.2s ease;
}

.device-list-item:hover {
  background: #f8fafc;
}

.device-list-item:last-child {
  border-bottom: none;
}

/* Device info layout */
.device-main-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.device-main-info strong {
  color: #D2691E;
  font-size: 1rem;
}

.device-alias {
  color: #64748b;
  font-size: 0.875rem;
  font-style: italic;
}

/* Device stats */
.device-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.device-stat-badge {
  display: inline-block;
  padding: 3px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.device-stat-badge.volume {
  background: #e0f2fe;
  color: #0369a1;
  border-color: #7dd3fc;
}

.device-stat-badge.status-processed {
  background: #dcfce7;
  color: #14532d;
  border-color: #86efac;
}

.device-stat-badge.status-pending {
  background: #fef3c7;
  color: #78350f;
  border-color: #fde047;
}

.device-stat-badge.status-error {
  background: #fee2e2;
  color: #7f1d1d;
  border-color: #fca5a5;
}

/* No devices message */
.no-devices {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-style: italic;
}

/* Visual hint for clickable card */
.clickable-stat::after {
  content: "📋";
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.875rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.clickable-stat:hover::after {
  opacity: 0.7;
}

/* Scrollbar styling for device list */
.device-list-container::-webkit-scrollbar {
  width: 6px;
}

.device-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.device-list-container::-webkit-scrollbar-thumb {
  background: #D2691E;
  border-radius: 3px;
}

.device-list-container::-webkit-scrollbar-thumb:hover {
  background: #8B4513;
}

/* Volume summary section */
.volume-summary {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-label {
  color: #64748b;
  font-size: 0.875rem;
}

.summary-value {
  font-weight: 600;
  color: #D2691E;
  font-size: 1rem;
}

/* Volume list container */
.volume-list-container {
  max-height: 400px;
  overflow-y: auto;
}

.volume-list-container h4 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Volume item */
.volume-item {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.2s ease;
}

.volume-item:hover {
  background: #f8fafc;
}

.volume-item:last-child {
  border-bottom: none;
}

/* Volume device info */
.volume-device-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.device-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-name strong {
  color: #1e293b;
  font-size: 0.875rem;
}

/* Volume amount */
.volume-amount {
  display: flex;
  align-items: center;
  gap: 6px;
}

.volume-value {
  font-weight: 700;
  color: #D2691E;
  font-size: 1rem;
}

.volume-percentage {
  color: #64748b;
  font-size: 0.75rem;
}

/* Volume bar visualization */
.volume-bar-container {
  width: 100%;
  height: 20px;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.volume-bar {
  height: 100%;
  background: linear-gradient(90deg, #3498DB 0%, #2196F3 100%);
  transition: width 0.3s ease;
  border-radius: 10px;
}

/* Volume details */
.volume-details {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.detail-badge {
  display: inline-block;
  padding: 3px 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.7rem;
  color: #475569;
}

/* No volume message */
.no-volume {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-style: italic;
}

/* Visual water drop animation for volume card */
.clickable-stat:has(.stat-label:contains("Volume"))::after {
  content: "💧";
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.875rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  animation: water-drop 2s infinite;
}

@keyframes water-drop {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.clickable-stat:has(.stat-label:contains("Volume")):hover::after {
  opacity: 0.7;
}

/* Scrollbar styling for volume list */
.volume-list-container::-webkit-scrollbar {
  width: 6px;
}

.volume-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.volume-list-container::-webkit-scrollbar-thumb {
  background: #3498DB;
  border-radius: 3px;
}

.volume-list-container::-webkit-scrollbar-thumb:hover {
  background: #2196F3;
}

/* Events summary section */
.events-summary {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
}

/* Events list container */
.events-list-container {
  max-height: 450px;
  overflow-y: auto;
}

.events-list-container h4 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Events device item */
.events-device-item {
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.events-device-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Device header */
.device-header {
  padding: 12px;
  background: white;
  cursor: pointer;
  transition: background 0.2s ease;
}

.device-header:hover {
  background: #f8fafc;
}

.device-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.device-name-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-name-section strong {
  color: #D2691E;
  font-size: 0.9rem;
}

.device-count {
  display: flex;
  align-items: center;
  gap: 8px;
}

.event-count-badge {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.expand-icon {
  color: #64748b;
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

/* Device progress bar */
.device-progress-bar {
  width: 100%;
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #D2691E 0%, #FF8C00 100%);
  transition: width 0.3s ease;
}

/* Device events detail */
.device-events-detail {
  padding: 12px;
  background: #fafbfc;
  border-top: 1px solid #e2e8f0;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Event type grid */
.event-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.event-type-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.event-type-item .activity-badge {
  font-size: 0.65rem;
  padding: 2px 6px;
}

.event-count {
  font-weight: 600;
  color: #334155;
  font-size: 0.875rem;
}

/* Device timeline */
.device-timeline {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  padding: 6px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.timeline-label {
  color: #64748b;
  font-size: 0.7rem;
  text-transform: uppercase;
}

.timeline-value {
  color: #1e293b;
  font-size: 0.75rem;
  font-weight: 500;
}

/* No events message */
.no-events {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-style: italic;
}

/* Scrollbar styling */
.events-list-container::-webkit-scrollbar {
  width: 6px;
}

.events-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.events-list-container::-webkit-scrollbar-thumb {
  background: #D2691E;
  border-radius: 3px;
}

.events-list-container::-webkit-scrollbar-thumb:hover {
  background: #FF8C00;
}

.search-row {
  background: #fff3e0 !important;
}

.search-row th {
  background: transparent !important;
  padding: 6px !important;
  border-right: 1px solid #ffcc80 !important;
}

.column-search-input {
  width: 100%;
  padding: 5px 8px;
  border: 1px solid #ffcc80;
  border-radius: 4px;
  font-size: 0.75rem;
  background: white;
  color: #333;
  transition: all 0.2s;
  font-weight: normal;
  letter-spacing: normal;
  text-transform: none;
  box-sizing: border-box;
}

.column-search-input:focus {
  outline: none;
  border-color: #ff9800;
  box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.2);
}

.column-search-input::placeholder {
  color: #999;
  font-style: italic;
}

.clear-column-searches-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.clear-column-searches-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(255, 82, 82, 0.4);
}

/* Date Range Filter Styling */
.date-filter-group {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 8px 6px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.date-range-showing {
  font-size: 0.75rem;
  font-weight: 400;
  color: #64748b;
  text-transform: none;
  letter-spacing: 0;
  display: inline-block;
  margin-left: 6px;
  font-weight: normal;
}

.date-quick-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.date-quick-btn {
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  background: white;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.date-quick-btn:hover {
  border-color: #D2691E;
  color: #D2691E;
  background: #fff9f5;
}

.date-quick-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
  box-shadow: 0 2px 8px rgba(210, 105, 30, 0.2);
}
</style>